import React from "react";

type DiagnosticErrorBoundaryProps = {
  children: React.ReactNode;
  moduleName: string;
  routeName: string;
};

type DiagnosticErrorBoundaryState = {
  error: Error | null;
  componentStack: string;
};

export default class DiagnosticErrorBoundary extends React.Component<
  DiagnosticErrorBoundaryProps,
  DiagnosticErrorBoundaryState
> {
  state: DiagnosticErrorBoundaryState = {
    error: null,
    componentStack: "",
  };

  static getDerivedStateFromError(error: Error) {
    return { error, componentStack: "" };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const componentStack = errorInfo.componentStack || "";
    this.setState({ componentStack });
    console.error("[OrthoPrecision] render error boundary", {
      moduleName: this.props.moduleName,
      routeName: this.props.routeName,
      errorName: error.name,
      errorMessage: error.message,
      componentStack,
      stack: error.stack,
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.error) {
      return this.props.children;
    }

    const stackLines = (this.state.error.stack || "").split("\n").filter(Boolean).slice(0, 6);
    const summaryStack = stackLines.join("\n");
    const componentStack = this.state.componentStack.trim();

    return (
      <div style={styles.shell}>
        <div style={styles.card}>
          <p style={styles.badge}>Erro de render capturado</p>
          <h1 style={styles.title}>A tela foi protegida para evitar uma tela branca total.</h1>
          <p style={styles.text}>
            Modulo atual: <strong>{this.props.moduleName}</strong>
          </p>
          <p style={styles.text}>
            Rota atual: <strong>{this.props.routeName}</strong>
          </p>
          <p style={styles.text}>
            Erro: <strong>{this.state.error.name}</strong> - {this.state.error.message}
          </p>

          {summaryStack && (
            <pre style={styles.pre}>{summaryStack}</pre>
          )}

          {componentStack && (
            <pre style={styles.pre}>{componentStack}</pre>
          )}

          <div style={styles.actions}>
            <button style={styles.buttonPrimary} onClick={this.handleReload}>
              Recarregar
            </button>
          </div>
        </div>
      </div>
    );
  }
}

const styles: Record<string, React.CSSProperties> = {
  shell: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "radial-gradient(circle at top, #10284a 0%, #050b18 55%, #02040a 100%)",
    color: "#e5eefc",
    padding: "24px",
  },
  card: {
    width: "100%",
    maxWidth: "760px",
    borderRadius: "24px",
    border: "1px solid rgba(110, 168, 255, 0.25)",
    background: "rgba(7, 14, 28, 0.92)",
    boxShadow: "0 24px 80px rgba(0, 0, 0, 0.45)",
    padding: "24px",
    backdropFilter: "blur(18px)",
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: "999px",
    background: "rgba(11, 95, 255, 0.18)",
    border: "1px solid rgba(11, 95, 255, 0.35)",
    color: "#9ec1ff",
    fontSize: "12px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    marginBottom: "16px",
  },
  title: {
    fontSize: "22px",
    lineHeight: 1.2,
    margin: "0 0 12px",
    color: "#f3f7ff",
  },
  text: {
    margin: "0 0 8px",
    color: "#c8d6f2",
    fontSize: "14px",
    lineHeight: 1.5,
  },
  pre: {
    marginTop: "16px",
    padding: "16px",
    borderRadius: "16px",
    overflow: "auto",
    whiteSpace: "pre-wrap",
    background: "rgba(8, 17, 35, 0.96)",
    border: "1px solid rgba(110, 168, 255, 0.15)",
    color: "#d7e4ff",
    fontSize: "12px",
    lineHeight: 1.6,
  },
  actions: {
    display: "flex",
    gap: "12px",
    marginTop: "18px",
    flexWrap: "wrap",
  },
  buttonPrimary: {
    border: "none",
    borderRadius: "999px",
    padding: "12px 18px",
    background: "linear-gradient(90deg, #0b5fff 0%, #5da8ff 100%)",
    color: "#fff",
    fontWeight: 700,
    cursor: "pointer",
  },
};
